import React, { useContext, useEffect, useLayoutEffect, useMemo } from 'react'
import { IntegrationContext, IntegrationContextPayload } from '../../../../../../context/integrationContext';
import { GenericDataContext, GenericDataContextPayload } from '../../../../../../context/genericDataContext';
import { UserContext, UserContextPayload } from '../../../../../../context/userContext';
import { whatsAppPaymentConfiguration } from '../../../../../../models/axiosModel';
import { Navigate, useSearchParams } from 'react-router-dom';
import { channelsBreadcrumb, homeBreadcrumb, paymentConfiguration } from '../../../../../../utils/breadcrumb';
import { formatDateAccordingToTimezoneUsingMoment, getWabaName, isWhatsAppBusinessApp } from '../../../../../../utils/constants';
import { usePaymentConfigurationStore } from '../../../../../../store/paymentConfigurationStore';
import { useShallow } from 'zustand/react/shallow';
import { Grid } from '@mui/material';
import { GlobalTable } from '../../../../../utility/globalTable';
import { findWhatsAppIntegration } from '../../../../../../pages/ruleBasedInbox/utils';

const PaymentConfiguration = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const pathname = window.location.pathname.toLowerCase();

    const { getMetaIntegrationList, integrationListNewStructure, setIntegrationDetails } = useContext(IntegrationContext) as IntegrationContextPayload;
    const { setBreadCrumbs } = useContext(GenericDataContext) as GenericDataContextPayload;
    const { setFlagLoading, handleError, userDetail } = useContext(UserContext) as UserContextPayload;

    const { getPaymentConfigurationApi, paymentConfigurationData } = usePaymentConfigurationStore(
        useShallow((store) => ({
            getPaymentConfigurationApi: store.getPaymentConfigurationApi,
            paymentConfigurationData: store.paymentConfigurationData
        }))
    )

    const currentIntegration = useMemo(() => {
        if (!integrationListNewStructure?.length || !id) return null;
        return findWhatsAppIntegration(integrationListNewStructure, id);
    }, [integrationListNewStructure, id]);

    const isSmbWaba = isWhatsAppBusinessApp(currentIntegration);
    const isIntegrationListLoaded = Array.isArray(integrationListNewStructure);
    const canAccessPaymentConfiguration = isIntegrationListLoaded && !!currentIntegration && !isSmbWaba;

    const getPaymentConfigurationsApiCall = async (wabaId: string) => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const res = await getPaymentConfigurationApi(wabaId);
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    const getMetaIntegrationListApi = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const res = await getMetaIntegrationList(true)
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    useLayoutEffect(() => {
        if (!integrationListNewStructure) {
            getMetaIntegrationListApi();
        }
    }, []);

    useEffect(() => {
        if (!canAccessPaymentConfiguration || !currentIntegration) return;

        (async () => {
            await getPaymentConfigurationsApiCall(currentIntegration.waba_id);
            setIntegrationDetails(currentIntegration);
        })()
    }, [canAccessPaymentConfiguration, currentIntegration])

    useEffect(() => {
        if (!canAccessPaymentConfiguration || !id) return;

        setBreadCrumbs([
            homeBreadcrumb,
            { ...channelsBreadcrumb },
            { name: getWabaName(integrationListNewStructure, Number(id), "Whatsapp"), path: `/channels/viewintegration/Whatsapp?id=${id}`, isActive: false, isSamePageRedirecting: pathname === "/channels/viewintegration/whatsapp" },
            { ...paymentConfiguration, isActive: true },
        ]);
    }, [canAccessPaymentConfiguration, id, integrationListNewStructure])

    // Close route for SMB WABA — redirect to Account Setup of the same WABA
    if (isIntegrationListLoaded && id && currentIntegration && isSmbWaba) {
        return <Navigate to={`/channels/viewintegration/Whatsapp?id=${id}`} replace />;
    }

    // Invalid / missing WABA id — send to account setup
    if (isIntegrationListLoaded && (!id || !currentIntegration)) {
        return <Navigate to="/channels/viewintegration/Whatsapp" replace />;
    }

    // Wait until integration list is loaded before rendering / calling payment APIs
    if (!canAccessPaymentConfiguration) {
        return null;
    }

    const paymentConfigurationColumns = [
        {
            name: "Configuration Name",
            alignment: "left",
            flagIcon: false,
            valueKey: 'whatsapp_payment_configuration_name'
        },
        {
            name: "Provider Name",
            alignment: "left",
            flagIcon: false,
            valueKey: 'whatsapp_payment_configuration_provider_name',
            iconKey: "documentIco",
        },
        {
            name: "Status",
            alignment: "left",
            flagIcon: false,
            flagHtml: true,
            valueKey: 'status_name',
            iconKey: "documentIco",
        },
        {
            name: "Last Updated",
            alignment: "left",
            flagIcon: false,
            valueKey: "timestamp",
            iconKey: "documentIco",
        }
    ]

    const getUpdatedPaymentConfigurationData = () => {
        if (paymentConfigurationData) {
            return paymentConfigurationData?.map((configuration: whatsAppPaymentConfiguration) => {
                return {
                    ...configuration,
                    timestamp: formatDateAccordingToTimezoneUsingMoment(configuration?.timestamp, userDetail?.timezone_hour)
                }
            })
        }
        return [];
    }

    return (
        <>
            <Grid item xs={12} paddingBlock={2}>
                <GlobalTable
                    headerList={paymentConfigurationColumns}
                    rowData={getUpdatedPaymentConfigurationData()}
                    tableContainerMinHeight={"auto"}
                    onRowClick={(e: React.SyntheticEvent) => {}}
                    rowAllowEdit={false}
                    flagPagination
                    tableHeight={"82vh"}
                />
            </Grid>
        </>
    )
}

export default PaymentConfiguration
